/** Regras puras de bosses, invocação de adds e resolução de golpes AoE. */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const slug = (value) => String(value || '').trim().toLowerCase().replace(/_/g, '-');
const randomUnit = (random) => clamp(Number(random()) || 0, 0, 0.999999);

/** Bosses / mini-bosses de zona existem a partir do Nv.200 (até 500). */
export function bossProfileFor(species, level) {
  const bossLevel = clamp(Math.floor(Number(level) || 200), 200, 500);
  const bst = clamp(Number(species?.bst) || 420, 180, 780);
  const types = Array.isArray(species?.types) && species.types.length ? species.types : ['normal'];
  const count = bossLevel >= 400 ? 3 : bossLevel >= 250 ? 2 : 1;
  const hpScale = 5 + (bossLevel - 200) / 50;
  const hp = Math.round((120 + bossLevel * 13 + bst * 0.8) * hpScale);
  const statMult = Math.round((1.35 + (bossLevel - 200) / 500) * 100) / 100;

  return Object.freeze({
    hp,
    statMult,
    summons: Object.freeze([{ type: types[0], count }]),
    rewards: Object.freeze({
      coinsMult: Math.round((3 + (bossLevel - 200) / 50) * 100) / 100,
      xpMult: Math.round((2.5 + (bossLevel - 200) / 80) * 100) / 100,
      shinyCardChance: Math.round((0.12 + (bossLevel - 200) * 0.001) * 1000) / 1000,
      pheromoneChance: Math.round((0.10 + (bossLevel - 200) * 0.0008) * 1000) / 1000,
      tmChance: Math.round((0.08 + (bossLevel - 200) * 0.0007) * 1000) / 1000,
      stoneRolls: bossLevel >= 400 ? 3 : bossLevel >= 250 ? 2 : 1,
    }),
  });
}

/**
 * Seleciona adds do mesmo tipo. A lista aceita entradas do Pokédex; o retorno
 * contém snapshots mínimos para a camada de combate instanciar.
 */
export function rollBossSummons(profile, speciesPool, {
  level = 200,
  random = Math.random,
  excludeSpecies = null,
} = {}) {
  const rows = Array.isArray(speciesPool) ? speciesPool : [];
  const summons = [];
  for (const group of profile?.summons || []) {
    const eligible = rows.filter((entry) => entry?.types?.includes(group.type)
      && slug(entry.slug) !== slug(excludeSpecies));
    if (!eligible.length) continue;
    for (let index = 0; index < Math.max(0, Math.floor(Number(group.count) || 0)); index++) {
      const entry = eligible[Math.floor(randomUnit(random) * eligible.length)];
      summons.push({
        species: entry.slug,
        type: group.type,
        level: clamp(Math.floor(Number(level) || 200) - 10 - index * 5, 1, 500),
        hpMult: 0.65,
        statMult: 0.85,
        summon: true,
      });
    }
  }
  return summons;
}

function combatStat(entity, key, fallback) {
  return Math.max(1, Number(entity?.stats?.[key] ?? entity?.[key]) || fallback);
}

/**
 * Resolve um golpe de área contra todos os inimigos e devolve snapshots novos.
 * `move.effectiveness` pode ser número ou função `(enemy) => multiplier`.
 */
export function resolveAoE(attacker, enemies, move) {
  const targets = Array.isArray(enemies) ? enemies : [];
  if (!move?.aoe && move?.targeting !== 'area') throw new RangeError('move_is_not_aoe');
  const level = clamp(Math.floor(Number(attacker?.level) || 1), 1, 300);
  const power = Math.max(1, Number(move?.power) || 1);
  const special = move?.category === 'special';
  const attack = combatStat(attacker, special ? 'spa' : 'atk', 10 + level * 2);
  const stab = attacker?.types?.includes(move?.type) ? 1.2 : 1;
  const authoredMultiplier = Math.max(0, Number(move?.damageMultiplier) || 1);

  return targets.map((enemy) => {
    const defense = combatStat(enemy, special ? 'spd' : 'def', 10 + (Number(enemy?.level) || 1) * 2);
    const effectiveness = typeof move?.effectiveness === 'function'
      ? Number(move.effectiveness(enemy)) || 1
      : Math.max(0, Number(move?.effectiveness) || 1);
    const raw = (((2 * level / 5 + 2) * power * attack / defense) / 50 + 2)
      * stab * authoredMultiplier * effectiveness;
    const damage = Math.max(1, Math.round(raw));
    // No cliente de hunt, `hp` é % da barra e `currentHp` é HP absoluto.
    // Em testes/unitários, `hp` ainda pode ser o valor absoluto.
    const hpBefore = Math.max(0, Number(
      Number.isFinite(enemy?.currentHp) ? enemy.currentHp
        : Number.isFinite(enemy?.maxHp) ? enemy.maxHp
          : enemy?.hp
    ) || 0);
    const hpAfter = Math.max(0, hpBefore - damage);
    return {
      ...enemy,
      hp: hpAfter,
      currentHp: hpAfter,
      damage,
      hpBefore,
      hpAfter,
      defeated: hpAfter <= 0,
    };
  });
}
